import { Euler, Object3D, Quaternion, Vector3 } from 'three';

import { CornerSingleSidePOI, PlanarFacePOI, Point3D } from 'api/models';
import { formatLength } from 'app/modules/wallforming/components/scenes/objects/dimensions/core/dimensionFormat';
import { DimensionText } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionText';
import { isCornerSingleSidePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import { DIMENSION_COLOR, EPSILON } from 'shared/constants';

import type { DimensionUnit } from 'app/modules/wallforming/components/scenes/objects/dimensions/types/dimensionSettings';

export const toFaceA = new Vector3();
export const toFaceB = new Vector3();

const RAD_TO_DEG = 180 / Math.PI;
const LABEL_OFFSET = 0.2;
const CORNER_OFFSET = 0.3;
const PLAN_LINE_LENGTH = 0.55;
const ANGLE_ARC_SEGMENTS = 16;
const FULL_CIRCLE_DEGREES = 360;
const FONT_SIZE = 0.1;
const VECTOR_UP_X = new Vector3(1, 0, 0);

const topPoint = new Vector3();
const bottomPoint = new Vector3();
const edgeDirection = new Vector3();
const verticalDirection = new Vector3(0, 0, 1);
const labelPosition = new Vector3();
const center = new Vector3();
const edgeStart = new Vector3();
const edgeEnd = new Vector3();
const edgeNormal = new Vector3();
const crossDirection = new Vector3();
const wallDirectionA = new Vector3();
const wallDirectionB = new Vector3();
const angleDirectionA = new Vector3();
const angleDirectionB = new Vector3();
const heightDirection = new Vector3();
const outsideDirection = new Vector3();
const arcDirection = new Vector3();
const navigationOffset = new Vector3();
const pointA = new Vector3();
const pointB = new Vector3();
const faceAverage = new Vector3();
const midpoint = new Vector3();

export function removeDuplicates(points: Vector3[], eps: number = EPSILON) {
    return points.filter(
        (p, i, arr) =>
            arr.findIndex(
                (q) => Math.abs(p.x - q.x) < eps && Math.abs(p.y - q.y) < eps && Math.abs(p.z - q.z) < eps
            ) === i
    );
}

function buildPlaneBasis(planeNormal: Vector3) {
    const n = planeNormal.clone().normalize();
    const fallback = Math.abs(n.x) > 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
    const u = new Vector3().crossVectors(n, fallback).normalize();
    const v = new Vector3().crossVectors(n, u).normalize();

    return { u, v };
}

export function sortVerticesInPlane(verts: Vector3[], planeNormal: Vector3) {
    if (!verts.length) {
        return [];
    }

    const { u, v } = buildPlaneBasis(planeNormal);
    const anchor = verts[0].clone();
    const coords = verts.map((p) => {
        const rel = p.clone().sub(anchor);
        return { p, x: rel.dot(u), y: rel.dot(v) };
    });

    const avgX = coords.reduce((s, q) => s + q.x, 0) / coords.length;
    const avgY = coords.reduce((s, q) => s + q.y, 0) / coords.length;
    coords.sort((a, b) => Math.atan2(a.y - avgY, a.x - avgX) - Math.atan2(b.y - avgY, b.x - avgX));

    return coords.map((q) => q.p);
}

function projectNavigationDirection(navigationPoint: Vector3, origin: Vector3, edgeDirection: Vector3) {
    return navigationPoint.clone().sub(origin).projectOnPlane(edgeDirection).normalize();
}

/**
 * Determines the corner rotation direction, preferring the outward-facing direction away from the building.
 */
export function getCornerRotationDir(selectedPoi: CornerSingleSidePOI) {
    // Calculate edge direction
    const bottomPoint = new Vector3(selectedPoi.bottomPoint.x, selectedPoi.bottomPoint.y, selectedPoi.bottomPoint.z);
    const edgeDirection = new Vector3(selectedPoi.topPoint.x, selectedPoi.topPoint.y, selectedPoi.topPoint.z)
        .sub(bottomPoint)
        .normalize();

    if (edgeDirection.lengthSq() < EPSILON) {
        return null;
    }

    // Project both navigation points onto the plane perpendicular to the edge.
    pointA.set(
        selectedPoi.faceA.navigationPoint.x,
        selectedPoi.faceA.navigationPoint.y,
        selectedPoi.faceA.navigationPoint.z
    );
    const dirA = projectNavigationDirection(pointA, bottomPoint, edgeDirection).negate();

    pointB.set(
        selectedPoi.faceB.navigationPoint.x,
        selectedPoi.faceB.navigationPoint.y,
        selectedPoi.faceB.navigationPoint.z
    );
    const dirB = projectNavigationDirection(pointB, bottomPoint, edgeDirection).negate();

    // Calculate bisector direction
    const heightDirection = dirA.add(dirB);
    if (heightDirection.lengthSq() < EPSILON) {
        return new Vector3(
            selectedPoi.faceA.formFace.normal.x,
            selectedPoi.faceA.formFace.normal.y,
            selectedPoi.faceA.formFace.normal.z
        )
            .cross(Object3D.DEFAULT_UP)
            .normalize();
    }

    // Calculate the outward-facing direction away from the building
    const outsideDirection = new Vector3(
        selectedPoi.faceA.formFace.normal.x + selectedPoi.faceB.formFace.normal.x,
        selectedPoi.faceA.formFace.normal.y + selectedPoi.faceB.formFace.normal.y,
        selectedPoi.faceA.formFace.normal.z + selectedPoi.faceB.formFace.normal.z
    ).projectOnPlane(edgeDirection);

    if (outsideDirection.lengthSq() >= EPSILON) {
        outsideDirection.normalize();
        if (heightDirection.dot(outsideDirection) < 0) {
            heightDirection.negate();
        }
    }

    return edgeDirection.cross(heightDirection.normalize()).normalize();
}

function getFaceNormal(face: PlanarFacePOI, target: Vector3): Vector3 {
    return target.set(face.formFace.normal.x, face.formFace.normal.y, face.formFace.normal.z).normalize();
}

export function getWallDirection(edge: Vector3, navigationPoint: Point3D, origin: Vector3, target: Vector3) {
    navigationOffset.set(navigationPoint.x, navigationPoint.y, navigationPoint.z).sub(origin).projectOnPlane(edge);
    target.copy(navigationOffset).normalize();
    return target;
}

export function computeCornerAngle(corner: CornerSingleSidePOI, edgeDirection: Vector3, edgeHeight: number): number {
    const normalA = getFaceNormal(corner.faceA, toFaceA);
    const normalB = getFaceNormal(corner.faceB, toFaceB);

    const raw = Math.acos(Math.max(-1, Math.min(1, normalA.dot(normalB)))) * RAD_TO_DEG;
    midpoint
        .set(corner.bottomPoint.x, corner.bottomPoint.y, corner.bottomPoint.z)
        .addScaledVector(edgeDirection, edgeHeight / 2);

    pointA.set(corner.faceA.navigationPoint.x, corner.faceA.navigationPoint.y, corner.faceA.navigationPoint.z);
    pointB.set(corner.faceB.navigationPoint.x, corner.faceB.navigationPoint.y, corner.faceB.navigationPoint.z);

    faceAverage.copy(pointA).add(pointB).multiplyScalar(0.5).sub(midpoint);
    return normalA.add(normalB).dot(faceAverage) >= 0 ? 180 - raw : 180 + raw;
}

export function getCornerPoi(selectedPoiIndex: number | undefined, state: PlacementState): CornerSingleSidePOI | null {
    if (selectedPoiIndex === undefined) {
        return null;
    }
    const poi = state.pois[selectedPoiIndex]?.poi;
    return poi && isCornerSingleSidePoi(poi) ? poi : null;
}

/**
 * Creates the geometric foundation for corner dimensioning.
 *
 * This includes:
 * - edge center and length
 * - wall directions
 * - outward dimension direction
 * - dimension line positions
 * - edge label placement
 * - label rotation
 */
export function createCornerGeometry(poi: CornerSingleSidePOI, mid: Vector3, dir: Vector3, heightScale = 0.4) {
    topPoint.set(poi.topPoint.x, poi.topPoint.y, poi.topPoint.z);
    bottomPoint.set(poi.bottomPoint.x, poi.bottomPoint.y, poi.bottomPoint.z);

    const edgeLength = topPoint.distanceTo(bottomPoint);
    if (edgeLength < EPSILON) {
        return null;
    }

    center.copy(topPoint).add(bottomPoint).multiplyScalar(0.5);
    edgeDirection.copy(dir).normalize();

    edgeNormal.crossVectors(edgeDirection, verticalDirection);
    if (edgeNormal.lengthSq() < EPSILON) {
        edgeNormal.set(1, 0, 0);
    } else {
        edgeNormal.normalize();
    }

    const faceA = poi.faceA;
    const faceB = poi.faceB;
    toFaceA.set(faceA.formFace.normal.x, faceA.formFace.normal.y, faceA.formFace.normal.z).normalize();
    toFaceB.set(faceB.formFace.normal.x, faceB.formFace.normal.y, faceB.formFace.normal.z).normalize();

    const origin = bottomPoint;
    getWallDirection(edgeDirection, faceA.navigationPoint, origin, wallDirectionA);
    getWallDirection(edgeDirection, faceB.navigationPoint, origin, wallDirectionB);

    wallDirectionA.negate();
    wallDirectionB.negate();

    // Calculate bisector direction
    heightDirection.copy(wallDirectionA).add(wallDirectionB);
    if (heightDirection.lengthSq() < EPSILON) {
        heightDirection.copy(edgeNormal);
    } else {
        heightDirection.normalize();
    }

    // Calculate the outward-facing direction away from the building
    outsideDirection.copy(toFaceA).add(toFaceB).projectOnPlane(edgeDirection);
    if (outsideDirection.lengthSq() >= EPSILON) {
        outsideDirection.normalize();

        if (heightDirection.dot(outsideDirection) < 0) {
            heightDirection.negate();
            wallDirectionA.negate();
            wallDirectionB.negate();
        }
    }

    // Handle height dimension
    edgeStart.copy(bottomPoint).sub(mid).addScaledVector(heightDirection, CORNER_OFFSET);
    edgeEnd.copy(edgeStart).addScaledVector(heightDirection, edgeLength * heightScale);

    // Handle text positions
    labelPosition.copy(edgeStart).lerp(edgeEnd, 0.5);
    const textDir = edgeDirection.clone().cross(heightDirection).normalize();
    labelPosition.addScaledVector(textDir, LABEL_OFFSET);

    // Handle text rotation
    const quaternion = new Quaternion().setFromUnitVectors(VECTOR_UP_X, textDir);
    const textUp = new Vector3(0, 1, 0).applyQuaternion(quaternion);
    if (textUp.dot(verticalDirection) < 0) {
        quaternion.multiply(new Quaternion().setFromAxisAngle(textDir, Math.PI));
    }
    const textRotation = new Euler().setFromQuaternion(quaternion);

    return {
        center: center.clone().sub(mid),
        edgeLength,
        edgeDirection: edgeDirection.clone(),
        wallDirectionA: wallDirectionA.clone(),
        wallDirectionB: wallDirectionB.clone(),
        heightDirection: heightDirection.clone(),
        edgeStart: edgeStart.clone(),
        edgeEnd: edgeEnd.clone(),
        labelPosition: labelPosition.clone(),
        textRotation
    };
}

/**
 * Creates the visualization of the corner angle.
 */
export function createAngleVisualization(
    poi: CornerSingleSidePOI,
    edgeEnd: Vector3,
    edgeDirection: Vector3,
    wallDirectionA: Vector3,
    wallDirectionB: Vector3,
    edgeLength: number
) {
    const angle = FULL_CIRCLE_DEGREES - computeCornerAngle(poi, edgeDirection, edgeLength);

    angleDirectionA.copy(wallDirectionA);
    angleDirectionB.copy(wallDirectionB);
    crossDirection.crossVectors(angleDirectionB, angleDirectionA);

    // Create visualization of the corner's two surrounding faces
    const faceLines = [
        edgeEnd.clone(),
        edgeEnd.clone().addScaledVector(angleDirectionB, PLAN_LINE_LENGTH),
        edgeEnd.clone(),
        edgeEnd.clone().addScaledVector(angleDirectionA, PLAN_LINE_LENGTH)
    ];

    // Calculate arc sweep between the surrounding faces
    const sweep = Math.atan2(crossDirection.dot(edgeDirection), angleDirectionB.dot(angleDirectionA));

    // Calculate points to display the angle arc
    const angleArc: Vector3[] = [];

    for (let i = 0; i <= ANGLE_ARC_SEGMENTS; i++) {
        arcDirection.copy(angleDirectionB).applyAxisAngle(edgeDirection, (sweep * i) / ANGLE_ARC_SEGMENTS);

        angleArc.push(edgeEnd.clone().addScaledVector(arcDirection, PLAN_LINE_LENGTH * 0.5));
    }

    // Handle text position
    arcDirection.copy(angleDirectionB).add(angleDirectionA);
    if (arcDirection.lengthSq() < EPSILON) {
        arcDirection.copy(angleDirectionB);
    } else {
        arcDirection.normalize();
    }

    const angleLabelPosition = edgeEnd.clone().addScaledVector(arcDirection, PLAN_LINE_LENGTH * 0.31);

    return {
        angle,
        faceLines,
        angleArc,
        angleLabelPosition
    };
}

/**
 * Creates all labels used for corner dimensioning.
 */
export function createEdgeDimensionTexts(
    poiGuid: string,
    edgeLength: number,
    edgeLabelPosition: Vector3,
    angle: number,
    angleLabelPosition: Vector3,
    unit: DimensionUnit
): DimensionText[] {
    return [
        {
            id: `${poiGuid}-edge-length`,
            position: edgeLabelPosition.clone(),
            text: formatLength(edgeLength, unit),
            fontSize: FONT_SIZE,
            color: DIMENSION_COLOR
        },
        {
            id: `${poiGuid}-angle`,
            position: angleLabelPosition.clone(),
            text: `${Math.round(angle)}°`,
            fontSize: FONT_SIZE * 0.9,
            color: DIMENSION_COLOR
        }
    ];
}
